/**
 * ApprovalButton - json-render component
 * 
 * Inline button for plan approval actions.
 * Used in chat messages to trigger plan approval workflow.
 */

import { useState } from 'react';
import { CheckCircle, Loader2 } from 'lucide-react';

export interface ApprovalButtonProps {
    planId: string;
    label?: string;
    disabled?: boolean;
    onApprove?: () => void;
}

export default function ApprovalButton({
    planId,
    label = 'Approve Plan',
    disabled = false,
    onApprove
}: ApprovalButtonProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [isApproved, setIsApproved] = useState(false);

    const handleClick = async () => {
        if (isLoading || isApproved || disabled) return;

        setIsLoading(true);
        try {
            if (onApprove) {
                await onApprove();
            }
            setIsApproved(true);
        } catch (error) {
            console.error('Approval failed for plan', planId, ':', error);
        } finally {
            setIsLoading(false);
        }
    };

    if (isApproved) {
        return (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm font-medium">Plan Approved</span>
            </div>
        );
    }

    return (
        <button
            onClick={handleClick}
            disabled={isLoading || disabled}
            className={`
                inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md
                bg-purple-600 hover:bg-purple-700 text-white font-medium text-sm
                transition-all duration-200
                disabled:opacity-50 disabled:cursor-not-allowed
            `}
        >
            {isLoading ? (
                <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Approving...
                </>
            ) : (
                <>
                    <CheckCircle className="h-4 w-4" />
                    {label}
                </>
            )}
        </button>
    );
}
